import { NgModule } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import { RouterModule } from '@angular/router';
import { DragDropModule } from '@angular/cdk/drag-drop';

// PrimeNG aggregate module
import { PrimeNgModule } from '../../primeng.module';

// Additional PrimeNG modules used across features
import { ChipsModule } from 'primeng/chips';
import { TieredMenuModule } from 'primeng/tieredmenu';
import { TreeTableModule } from 'primeng/treetable';
import { TabViewModule } from 'primeng/tabview';
import { SelectButtonModule } from 'primeng/selectbutton';
import { RadioButtonModule } from 'primeng/radiobutton';
import { AutoCompleteModule } from 'primeng/autocomplete';
import { ToastModule } from 'primeng/toast';
import { ProgressBarModule } from 'primeng/progressbar';
import { ProgressSpinnerModule } from 'primeng/progressspinner';
import { CalendarModule } from 'primeng/calendar';
import { CardModule } from 'primeng/card';
import { InputNumberModule } from 'primeng/inputnumber';

// Angular Material modules used across features
import { MatButtonModule } from '@angular/material/button';
import { MatTooltipModule } from '@angular/material/tooltip';
import { MatSelectModule } from '@angular/material/select';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';

// 3rd party modules used across multiple feature modules
import { NgxPrintModule } from 'ngx-print';
import { YouTubePlayerModule } from '@angular/youtube-player';
import { QRCodeModule } from 'angularx-qrcode';
import { RecaptchaModule, RecaptchaFormsModule } from 'ng-recaptcha';

// Shared Pipes
import { TranslatorPipe } from '../services/translator.pipe';
import { SafeHTMLPipe } from '../misc/pipes/safe-html.pipe';
import { HighlightSearch } from '../services/highlight-text.pipe';

// Shared Directives
import { ImgOrIconDirective } from '../misc/directive/img-or-icon.directive';
import { PlainTextPasteDirective } from '../misc/component/common/plain-text-paste.directive';
import { NoInputSpaceDirective } from '../misc/directive/no-input-space.directive';
import { FilterButtonSuffix } from '../misc/directive/clear-button-suffix.directive';

// Shared Components
import { NoDataComponent } from '../no-data/no-data.component';
import { TableComponent } from '../misc/component/table/table.component';
import { FormComponent } from '../misc/component/form/form.component';
import { DynamicFormComponent } from '../misc/component/dynamic-form/dynamic-form.component';
import { DynamicFormQuestionComponent } from '../misc/component/dynamic-form/dynamic-form-question.component';
import { FileUploadComponent } from '../misc/component/common/file-upload/file-upload.component';
import { SkelitionsComponent } from '../admin/layout/skelitions/skelitions.component';
import { HeaderComponent } from '../admin/layout/header/header.component';

// Cross-module layout components
import { AssignUsersComponent } from '../assign-users/assign-users.component';
import { AddExternalUserComponent } from '../admin/views/company/add-external-user/add-external-user.component';
import { NavComponent } from '../layout/nav/nav.component';
import { SidenavComponent } from '../layout/sidenav/sidenav.component';
import { FooterComponent } from '../layout/footer/footer.component';
import { QuestionTemplateComponent } from '../admin/views/survey/question-template/question-template.component';
import { TrainingProgramViewComponent } from '../training-program-view/training-program-view.component';

// Cross-module feature components (used by multiple lazy modules)
import { SelectServiceComponent } from '../admin/views/data/add/select-service/select-service.component';
import { SignupFormComponent } from '../views/signup/signup-form/signup-form.component';
import { SurveyComponent } from '../admin/views/survey/survey.component';
import { SurveyTypeComponent } from '../admin/views/survey/survey-type/survey-type.component';
import { AssessmentComponent } from '../admin/views/assessment/assessment.component';
import { QuestionBankComponent } from '../admin/views/assessment/question-bank/question-bank.component';
import { QuestionBankListComponent } from '../admin/views/assessment/question-bank-list/question-bank-list.component';
import { AddQuestionComponent } from '../admin/views/survey/add-question/add-question.component';
import { QuestionsEditComponent } from '../admin/views/survey/add-question/questions-edit/questions-edit.component';
import { RatingGraphComponent } from '../admin/views/training/rating-graph/rating-graph.component';
import { TrainingCalanderComponent } from '../admin/views/training/training-calander/training-calander.component';
import { ShowDetailComponent } from '../admin/course/show-detail/show-detail.component';
import { TrainingUsersDetailComponent } from '../admin/course/training-users-detail/training-users-detail.component';
import { TrainingTimelineComponent } from '../training-timeline/training-timeline.component';
import { UserTableComponent } from '../admin/views/survey/user-table/user-table.component';
import { TrainingCertificateComponent } from '../training-page/training-certificate/training-certificate.component';
import { AssessmentResultModuleComponent } from '../admin/views/assessment/assessment-result-module/assessment-result-module.component';
import { AddTrainingKlComponent } from '../admin/views/training/add-training-kl/add-training-kl.component';
import { ReminderModalComponent } from '../reminder-modal/reminder-modal.component';
import { AssessmentResultsComponent } from '../admin/views/assessment/assessment-results/assessment-results.component';

const SHARED_DECLARATIONS = [
  // Pipes
  TranslatorPipe,
  SafeHTMLPipe,
  HighlightSearch,
  // Directives
  ImgOrIconDirective,
  PlainTextPasteDirective,
  NoInputSpaceDirective,
  FilterButtonSuffix,
  // Shared Components
  NoDataComponent,
  TableComponent,
  FormComponent,
  DynamicFormComponent,
  DynamicFormQuestionComponent,
  FileUploadComponent,
  SkelitionsComponent,
  HeaderComponent,
  // Layout components
  AssignUsersComponent,
  AddExternalUserComponent,
  NavComponent,
  SidenavComponent,
  FooterComponent,
  QuestionTemplateComponent,
  TrainingProgramViewComponent,
  // Cross-module feature components
  SelectServiceComponent,
  SignupFormComponent,
  SurveyComponent,
  SurveyTypeComponent,
  AssessmentComponent,
  QuestionBankComponent,
  QuestionBankListComponent,
  AddQuestionComponent,
  QuestionsEditComponent,
  RatingGraphComponent,
  TrainingCalanderComponent,
  ShowDetailComponent,
  TrainingUsersDetailComponent,
  TrainingTimelineComponent,
  UserTableComponent,
  TrainingCertificateComponent,
  AssessmentResultModuleComponent,
  AddTrainingKlComponent,
  ReminderModalComponent,
  AssessmentResultsComponent,
];

const SHARED_MODULES = [
  CommonModule,
  FormsModule,
  ReactiveFormsModule,
  RouterModule,
  PrimeNgModule,
  DragDropModule,
  // Additional PrimeNG
  ChipsModule,
  TieredMenuModule,
  TreeTableModule,
  TabViewModule,
  SelectButtonModule,
  RadioButtonModule,
  AutoCompleteModule,
  ToastModule,
  ProgressBarModule,
  ProgressSpinnerModule,
  CalendarModule,
  CardModule,
  InputNumberModule,
  // Angular Material
  MatButtonModule,
  MatTooltipModule,
  MatSelectModule,
  MatFormFieldModule,
  MatInputModule,
  // 3rd party
  NgxPrintModule,
  YouTubePlayerModule,
  QRCodeModule,
  RecaptchaModule,
  RecaptchaFormsModule,
];

@NgModule({
  declarations: [...SHARED_DECLARATIONS],
  imports: [...SHARED_MODULES],
  exports: [
    ...SHARED_MODULES,
    ...SHARED_DECLARATIONS,
  ],
})
export class SharedModule {}
